import * as THREE from 'three';
import { MISSION, SIM, SKY, SPAWN } from './config/tuning';
import { AIRFRAME_SCOUT } from './config/airframes';
import { Checksum } from './core/Checksum';
import { bus } from './core/EventBus';
import { Loop } from './core/Loop';
import { Registry } from './core/Registry';
import { NoseCamera } from './render/Camera';
import { Renderer } from './render/Renderer';
import { PostStack, type PostDrive } from './render/PostStack';
import { applyPostOverride, POST_EFFECT_KEYS } from './render/PostSettings';
import { computeTrim } from './flight/Aerodynamics';
import { Atmosphere } from './flight/Atmosphere';
import { Autopilot } from './flight/Autopilot';
import { ControlLock } from './flight/ControlLock';
import { FlightModel } from './flight/FlightModel';
import { Bindings, InputAction } from './input/Bindings';
import { InputManager } from './input/InputManager';
import { loadTerrain } from './world/Terrain';
import { TerrainRenderer } from './world/TerrainRenderer';
import { Ocean } from './world/Ocean';
import { Sky } from './world/Sky';
import { Scatter } from './world/Scatter';
import { Hud } from './hud/Hud';
import { createHudFrame } from './hud/HudFrame';
import { HudTelemetry, type ThreatTelemetry } from './hud/HudTelemetry';
import { ThreatField } from './threat/ThreatField';
import { TargetField } from './combat/TargetField';
import { TargetRenderer } from './world/TargetRenderer';
import { MarkerRenderer } from './world/MarkerRenderer';
import { Interceptor } from './threat/Interceptor';
import { ImpactCam } from './render/ImpactCam';
import { Countermeasures } from './threat/Countermeasures';
import { Effects } from './render/Effects';
import type { RadarTarget } from './threat/Detection';
import { ThreatDebug } from './debug/ThreatDebug';
import { DebugOverlay } from './debug/DebugOverlay';
import { PostPanel } from './debug/PostPanel';
import { FlightReadout } from './debug/FlightReadout';
import { TrailMarkers } from './debug/TrailMarkers';
import { formatSelfTestResult, runDeterminismSelfTest } from './debug/DeterminismSelfTest';
import { startHudTestScene } from './debug/HudTestScene';
import { startInterceptScene } from './debug/InterceptScene';
import { startImpactScene } from './debug/ImpactScene';
import { installStateBridge } from './debug/StateBridge';
import { loadCampaign, reportBroken } from './mission/MissionLoader';
import { MissionRunner } from './mission/MissionRunner';
import { buildFreeFlight } from './mission/Generated';
import { Soundscape } from './audio/Soundscape';
import { Save, type Settings } from './save/Save';
import { Screen, Shell } from './ui/Shell';
import { FlightRecorder } from './telemetry/Recorder';
import { buildDebrief } from './telemetry/Debrief';
import { ReplayPlayer } from './telemetry/Replay';
import type { Mission } from './mission/schema';

/** No photograph objectives are wired yet; the set is shared so the tick never allocates. */
const NO_PHOTOGRAPHS: ReadonlySet<string> = new Set<string>();

/**
 * M6 bootstrap: the Kerani theatre, the downlink, the threat picture, and missiles in the air.
 *
 * URL parameters:
 *   ?seed=0x5EED0001   master PRNG seed (decimal or 0x-prefixed)
 *   ?scene=hudtest     the HUD tuning page — sliders for every driving value, no flying
 *   ?scene=intercept   the missile intercept harness — one site, one missile, top-down plot
 *   ?selftest=1        run the determinism self-test at startup and log the result
 *   ?debug=1           show the debug overlay and the downlink panel immediately
 *   ?wind=0            still air, no gusts or turbulence
 *   ?contrast=1        start in the high-contrast HUD theme
 *   ?realistic=1       Realistic difficulty: look-around disabled (SPEC §3.3.1)
 *   ?hour=9.5          time of day, hours
 *   ?post=0            no post-processing at all; or a list, e.g. ?post=grade,haze,fxaa
 */

const DEG_TO_RAD = Math.PI / 180;

// Render-loop scratch. Allocating these per frame is exactly what SPEC §4.3 forbids.
const cameraPositionScratch = new THREE.Vector3();
const cameraOrientationScratch = new THREE.Quaternion();
const spawnPositionScratch = new THREE.Vector3();
/** Scratch for the debug attack spawn; module scope so the spawn path allocates nothing. */
const attackAxis = new THREE.Vector3();
const attackRotation = new THREE.Quaternion();

/** What the radars are looking at. Rewritten each tick; never re-allocated (SPEC §4.3). */
const radarTarget: RadarTarget = {
  positionM: new THREE.Vector3(),
  velocityMs: new THREE.Vector3(),
  forward: new THREE.Vector3(),
  rcsM2: 1,
  altitudeAglM: 0,
};

/** The threat picture as the HUD needs it. Rewritten each frame. */
const threatTelemetry: ThreatTelemetry = {
  jamming: 0,
  linkQuality: 1,
  rwrContacts: [],
  missileWarnings: [],
  chaffRemaining: 0,
  flaresRemaining: 0,
  gpsErrorM: new THREE.Vector2(),
};

/** Rewritten in place each frame and handed to the post stack; never re-allocated. */
const postDrive: PostDrive = {
  dtS: 0,
  simTimeS: 0,
  linkQuality: 1,
  yawRateRadS: 0,
  horizontalFovRad: 1,
  sunDirection: new THREE.Vector3(0, 1, 0),
  hazeColour: new THREE.Color(),
  overWater: false,
  altitudeM: 0,
};

/** Parse a decimal or 0x-prefixed integer, falling back when absent or malformed. */
function parseSeed(raw: string | null, fallback: number): number {
  if (raw === null || raw.trim() === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed >>> 0 : fallback;
}

function requireCanvas(id: string): HTMLCanvasElement {
  const element = document.getElementById(id);
  if (!(element instanceof HTMLCanvasElement)) {
    throw new Error(`VECTOR ZERO: #${id} canvas element is missing from index.html`);
  }
  return element;
}

async function main(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const seed = parseSeed(params.get('seed'), SIM.DEFAULT_SEED);

  const glCanvas = requireCanvas('gl');
  const hudCanvas = requireCanvas('hud');

  const renderer = new Renderer(glCanvas);
  const post = new PostStack();
  applyPostOverride(post.settings, params.get('post'));
  renderer.setPostStack(post);
  const camera = new NoseCamera();
  const hud = new Hud(hudCanvas);

  // --- Persistence and settings (SPEC §3.15). Settings apply before anything is drawn, so a
  // player who turned an effect off does not see one frame of it on every load.
  const save = new Save();
  const applySettings = (settings: Settings): void => {
    renderer.setRenderScale(settings.resolutionScale);
    for (const key of POST_EFFECT_KEYS) {
      const stored = settings.postEffects[key];
      if (stored !== undefined) post.settings[key] = stored;
    }
    // The accessibility switches override the individual effect toggles rather than sitting
    // beside them: a player who asked for no grain must not have it reappear because a preset or
    // a URL parameter turned it back on (SPEC §3.14 — "genuinely effective, not token").
    if (settings.disableChromaticAberration) post.settings.chromatic = false;
    if (settings.disableGrain) post.settings.grain = false;
    if (settings.reduceRollingShutter) post.settings.rollingShutter = false;
    if (settings.reduceCameraShake) post.settings.jitter = false;

    hud.setHighContrast(settings.highContrastHud);
    soundscape.setLevel('master', settings.masterVolume);
    soundscape.setLevel('effects', settings.effectsVolume);
    soundscape.setLevel('rwr', settings.rwrVolume);
    // RWR visual-only silences the tones but leaves the ring — the enlarged indicator is the
    // point of the option, not the silence.
    if (settings.rwrVisualOnly) soundscape.setLevel('rwr', 0);
    camera.lookAroundEnabled = settings.difficulty !== 'realistic';
  };
  hud.setHighContrast(params.get('contrast') === '1' || save.settings.highContrastHud);
  camera.lookAroundEnabled = params.get('realistic') !== '1';

  if (params.get('scene') === 'impact') {
    document.getElementById('boot')?.classList.add('hidden');
    hudCanvas.style.pointerEvents = 'none';
    startImpactScene(hudCanvas);
    return;
  }

  if (params.get('scene') === 'intercept') {
    document.getElementById('boot')?.classList.add('hidden');
    hudCanvas.style.pointerEvents = 'none';
    startInterceptScene(hudCanvas);
    return;
  }

  if (params.get('scene') === 'hudtest') {
    document.getElementById('boot')?.classList.add('hidden');
    startHudTestScene(hud, camera, hudCanvas);
    return;
  }

  // --- World ------------------------------------------------------------------------------
  // The heightfield is a committed binary (SPEC §3.9.1); everything else is generated in code.
  setBootMessage('LOADING KERANI');
  const terrain = await loadTerrain(TERRAIN_URL);

  const sky = new Sky();
  sky.setTimeOfDay(Number(params.get('hour') ?? SKY.defaultTimeOfDayHours));
  // The downlink's haze veil and sun glare read straight off the sky, so there is exactly one
  // definition of where the light comes from (SPEC §3.9.3).
  postDrive.sunDirection = sky.sunDirection;
  postDrive.hazeColour = sky.hazeColour;
  const terrainRenderer = new TerrainRenderer(terrain, sky);
  const ocean = new Ocean(terrain, sky, terrainRenderer.heights);

  setBootMessage('PLACING SCATTER');
  const scatter = new Scatter(terrain);

  // Scene fog matches the shaders' own haze exactly (both are exponential-squared on the same
  // density), so instanced scatter dissolves into the distance with the terrain behind it.
  renderer.scene.fog = new THREE.FogExp2(sky.hazeColour.getHex(), sky.hazeDensity);

  renderer.scene.add(sky.object3d);
  renderer.scene.add(sky.sunLight);
  renderer.scene.add(sky.ambientLight);
  renderer.scene.add(terrainRenderer.object3d);
  renderer.scene.add(ocean.object3d);
  renderer.scene.add(scatter.object3d);

  const registry = new Registry();
  const trail = new TrailMarkers(registry);
  renderer.scene.add(trail.object3d);

  // --- Simulation ------------------------------------------------------------------------
  const atmosphere = new Atmosphere(seed);
  if (params.get('wind') === '0') {
    atmosphere.windMs.set(0, 0, 0);
    atmosphere.gustScale = 0;
    atmosphere.turbulenceScale = 0;
  }

  const airframe = AIRFRAME_SCOUT;
  const flight = new FlightModel(airframe, atmosphere);
  const autopilot = new Autopilot();
  const input = new InputManager(Bindings.load());

  flight.ground = terrain;
  const telemetry = new HudTelemetry(airframe, terrain, createHudFrame());

  // --- Mission (SPEC §3.10). The campaign is data; `?mission=` selects one, and Free Flight
  // stands in when nothing is named so the sandbox is still one keystroke away.
  const catalogue = loadCampaign();
  reportBroken(catalogue);
  const requestedMission = params.get('mission');
  let activeMission: Mission =
    requestedMission === 'free' || catalogue.campaign.length === 0
      ? buildFreeFlight()
      : (catalogue.byId(requestedMission ?? '') ?? catalogue.campaign[0]);
  let missionRunner = new MissionRunner(activeMission);

  // --- Threats (SPEC §3.5), placed by the mission.
  const threats = new ThreatField(terrain, atmosphere);
  threats.loadMission(activeMission);
  radarTarget.rcsM2 = airframe.rcsM2;
  const countermeasures = new Countermeasures();

  // --- Audio (SPEC §3.11). Silent until a user gesture unlocks the context; safe either way.
  const soundscape = new Soundscape();
  soundscape.setTerrain(terrain);
  soundscape.attachGestureUnlock(window);

  const threatDebug = new ThreatDebug(threats, terrain);
  renderer.scene.add(threatDebug.object3d);

  const effects = new Effects();
  renderer.scene.add(effects.object3d);

  // --- Targets (SPEC §3.8), placed by the mission.
  const targetField = new TargetField(terrain);
  targetField.loadMission(activeMission);
  const targetRenderer = new TargetRenderer(targetField.targets, seed);
  renderer.scene.add(targetRenderer.object3d);

  // World markers: the ring on the next checkpoint and a beacon on each target still standing.
  const markers = new MarkerRenderer();
  markers.rebuild(targetField.targets);
  renderer.scene.add(markers.object3d);
  /** Scratch for the per-frame checkpoint hand-off; see the render loop. */
  const checkpointScratch = { positionM: new THREE.Vector3(), radiusM: 0, groundM: 0 };

  // One interceptor, patrolling inland. Missions decide the fit at M8.
  const interceptor = new Interceptor();
  const impactCam = new ImpactCam();
  /** Seeds the deterministic damage spread, so a replay wounds the same systems. */
  let impactCounter = 0;

  bus.on('target:impact', (hit) => {
    const overWater = terrain.surfaceHeightAt(hit.positionM.x, hit.positionM.z) < 0;
    if (overWater) effects.waterImpact(hit.positionM, hit.effect);
    else effects.explode(hit.positionM, hit.effect, terrain.surfaceHeightAt(hit.positionM.x, hit.positionM.z));

    targetRenderer.update(targetField.targets);
    impactCam.start(hit.positionM);

    console.info(
      `${hit.grade}: ${hit.designation} (${hit.kind}) — ${hit.aimPointDistanceM.toFixed(1)} m from aim point, ` +
        `${hit.impactSpeedMs.toFixed(0)} m/s, ${hit.diveAngleDeg.toFixed(0)}° dive` +
        `${hit.failure === 'none' ? '' : ` — ${hit.failure}`}`,
    );
  });

  bus.on('threat:launch', (launch) => {
    console.info(
      `THREAT: ${launch.designation} launched (salvo ${launch.salvoIndex + 1}) at T+${loop.time.simTimeS.toFixed(1)}s`,
    );
  });

  bus.on('missile:detonated', (hit) => {
    // SPEC §3.6.4: graded damage, and a wounded drone that can still fly is the best outcome the
    // game produces — so this is a hand-off to the same damage model an over-g pull uses.
    // Where the warhead went off decides which systems it takes (SPEC §3.8, M7) — a blast off the
    // left wing leaves a different aircraft from one off the tail.
    flight.applyBlast(hit.positionM, hit.damage, impactCounter);
    impactCounter += 1;
    effects.detonate(hit.positionM, hit.damage);
    console.info(
      `IMPACT: ${hit.designation} fused at ${hit.missDistanceM.toFixed(1)} m — ` +
        `${(hit.damage * 100).toFixed(0)}% damage, integrity ${(flight.state.integrity * 100).toFixed(0)}%`,
    );
  });

  bus.on('missile:expired', (miss) => {
    effects.detonate(miss.positionM, 0);
    console.info(`MISS: ${miss.designation} self-destructed at ${miss.missDistanceM.toFixed(0)} m`);
  });

  // --- Shell, recorder and replay (SPEC §3.14, §3.15).
  const recorder = new FlightRecorder();
  const replay = new ReplayPlayer(recorder);
  let replaying = false;

  /** Rebuild the world for a mission. Used by the shell when the player picks one. */
  const loadMission = (mission: Mission): void => {
    activeMission = mission;
    threats.resetLayout();
    threats.loadMission(mission);
    targetField.resetLayout();
    targetField.loadMission(mission);
    targetRenderer.rebuild(targetField.targets);
    markers.rebuild(targetField.targets);
    missionRunner.dispose();
    missionRunner = new MissionRunner(mission);
    recorder.reset();
    replaying = false;
    spawnAircraft();
  };

  /**
   * Freeze or resume the mission from the pause menu.
   *
   * `loop.setPaused` rather than relying on the shell being up: the shell gate stops the systems
   * but leaves the loop's own clock running, which is why a paused game used to keep advancing
   * `simTimeS`. This stops time itself and keeps rendering at the frozen alpha, so the picture
   * behind the menu is the picture the player paused on.
   */
  const setPaused = (paused: boolean): void => {
    loop.setPaused(paused);
    soundscape.setMuted(paused);
    if (paused) {
      shell.show(Screen.Paused);
    } else {
      shell.hide();
      // Nothing pressed while the menu was up survives into the mission — including the Escape
      // that dismissed it.
      input.clearPendingActions();
    }
  };

  const shell = new Shell(catalogue, save, {
    onFly: (mission) => loadMission(mission),
    onFreeFlight: () => loadMission(buildFreeFlight()),
    onSettingsChanged: (settings) => applySettings(settings),
    onReplay: () => {
      replaying = true;
      replay.seek(0);
      replay.play();
      shell.hide();
    },
    onResume: () => setPaused(false),
    onRestart: () => {
      loadMission(activeMission);
      setPaused(false);
    },
    onAbandon: () => {
      // No debrief and no score: the mission was not flown to a conclusion, and recording a rank
      // for a run that was walked away from would make the campaign list lie.
      loop.setPaused(false);
      soundscape.setMuted(false);
      shell.show(Screen.MissionSelect);
    },
  });
  shell.setTerrain(terrain);
  // Applied once every system it touches exists — the post stack, the HUD and the soundscape are
  // all constructed above this point.
  applySettings(save.settings);

  // The debrief is built when the mission resolves, not when the player asks for it: the runner
  // has the score and the recorder has the flight, and both are about to be reset.
  bus.on('mission:outcome', (outcome) => {
    const report = buildDebrief({
      missionTitle: activeMission.title,
      success: outcome.success,
      outcomeReason: outcome.reason,
      score: missionRunner.score,
      objectives: missionRunner.objectives.statuses,
      recorder,
      elapsedS: missionRunner.timeS,
    });
    if (missionRunner.score !== null) {
      save.recordMission(activeMission.id, missionRunner.score.rank, missionRunner.score.total);
    }
    shell.showDebrief(report, recorder.groundTrack());
  });

  // Countermeasure and objective marks for the debrief timeline.
  bus.on('threat:launch', (launch) => {
    recorder.mark(loop.time.simTimeS, 'launch', launch.designation);
  });
  bus.on('missile:detonated', (hit) => {
    recorder.mark(loop.time.simTimeS, 'detonation', hit.designation, hit.missDistanceM);
  });
  bus.on('missile:expired', (miss) => {
    recorder.mark(loop.time.simTimeS, 'detonation', miss.designation, miss.missDistanceM);
  });
  bus.on('target:impact', (hit) => {
    recorder.mark(loop.time.simTimeS, 'impact', hit.designation, hit.aimPointDistanceM);
  });
  bus.on('mission:radio', (radio) => {
    recorder.mark(loop.time.simTimeS, 'radio', `${radio.speaker}: ${radio.text}`);
  });

  /** Threat designation → state label, rebuilt each evaluation for `threatState` triggers. */
  const threatStates = new Map<string, string>();

  /** Push the mission's live intent into the world: which sites are awake, which marks are shown. */
  const applyMissionEffects = (): void => {
    threats.applyActive(missionRunner.triggers.effects.activeThreats);
    telemetry.setMissionTags(
      missionRunner.visibleRoute(),
      targetField.targets.map((target) => ({
        designation: target.designation,
        label: target.label,
        positionM: target.aimPointM,
        destroyed: target.destroyed,
      })),
    );
  };

  /**
   * `?attack=<range>` — spawn already established on a terminal attack run (debug only).
   *
   * The terminal second of a mission is the least reachable part of the game to test: it is ten
   * kilometres and two minutes of correct flying away, and everything that makes it interesting —
   * the graded impact, the replay cam, the explosion — happens in the one second after arrival.
   * `?scene=impact` covers the *mechanics* headlessly; this covers the *presentation*, which needs
   * the real 3D scene and cannot be plotted.
   *
   * Optional dive angle via `?dive=`, defaulting to a comfortable 20°. The aircraft is placed on
   * the line, trimmed for the dive, and pointed at the first live target's aim point.
   */
  const attackRangeM = Number(params.get('attack') ?? '0');
  const attackDiveDeg = Number(params.get('dive') ?? '20');

  const controlLock = new ControlLock();
  /** Throttle the controls are held at during the start hold, 0..1. Set by each spawn. */
  let spawnThrottle = 0;

  const spawnAircraft = (): void => {
    const [x, y, z] = SPAWN.positionM;
    // Spawn trimmed. With a rate-command rotation model there is no pitch stiffness to find
    // trim on its own, so an untrimmed spawn hands the player an aircraft already sinking.
    // The mission owns the launch state (SPEC §6.1); SPAWN is only the fallback the harnesses use.
    const launch = activeMission.launch;
    let launchX = launch.position[0];
    let launchY = launch.altitudeM;
    let launchZ = launch.position[2];
    let launchHeadingRad = launch.headingDeg * DEG_TO_RAD;
    let launchSpeedMs = launch.airspeedMs;
    let launchDiveRad = 0;
    void x;
    void y;
    void z;

    if (attackRangeM > 0 && targetField.targets.length > 0) {
      const target = targetField.targets[0];
      const diveRad = attackDiveDeg * DEG_TO_RAD;
      // Approach from the west so the run matches the campaign's easterly ingress.
      const groundRangeM = attackRangeM * Math.cos(diveRad);
      launchX = target.aimPointM.x - groundRangeM;
      launchZ = target.aimPointM.z;
      launchY = target.aimPointM.y + attackRangeM * Math.sin(diveRad);
      launchHeadingRad = Math.PI / 2; // due east, straight at the aim point
      launchSpeedMs = Math.max(launch.airspeedMs, 130);
      launchDiveRad = diveRad;
      console.info(
        `ATTACK SPAWN — ${attackRangeM} m from ${target.designation} at ${attackDiveDeg}° dive, ` +
          `${launchSpeedMs} m/s`,
      );
    }

    const trim = computeTrim(airframe, atmosphere.densityAtM(launchY), launchSpeedMs);
    /**
     * Hand the trim throttle to the *input* as well as to the aircraft.
     *
     * `FlightModel` takes the input's throttle as authoritative every tick, and the input's
     * accumulator starts at zero — so a spawn that trimmed the aircraft for level flight then
     * commanded idle on the very first tick, and every mission began with the engine spooling
     * down. The tell was `ENDUR` reading ten times its real figure: `enduranceSeconds` divides by
     * a 0.05 idle floor. `setThrottle` was written for this and had never been called.
     */
    input.setThrottle(trim.throttle);
    // No queued edge survives into the new mission — an autopilot toggle pressed on the debrief
    // must not fire the moment the next one spawns.
    input.clearPendingActions();
    spawnThrottle = trim.throttle;
    // The `?attack=` harness spawns inside the terminal run: a five-second hold there would be
    // most of the flight, and nothing arrives from a briefing screen to need protecting from.
    controlLock.reset(attackRangeM > 0 ? 0 : MISSION.controlLockS);
    flight.spawn({
      positionM: spawnPositionScratch.set(launchX, launchY, launchZ),
      headingRad: launchHeadingRad,
      airspeedMs: launchSpeedMs,
      throttle: trim.throttle,
      pitchRad: trim.alphaRad,
    });
    /**
     * Tip the whole aircraft onto the dive line.
     *
     * `FlightState.spawn` lays velocity along the *horizontal* heading and treats `pitchRad` as
     * the initial angle of attack, not the flight path angle. Passing a dive angle there sets an
     * 80° alpha instead: the wing instantly exceeds the failure g and `destroyAirframe` fires
     * before the first frame is drawn. Rotating orientation *and* velocity by the same world-space
     * quaternion is what puts the flight path on the dive line while leaving alpha at trim.
     */
    if (launchDiveRad > 0) {
      attackRotation.setFromAxisAngle(attackAxis.copy(flight.state.right()), -launchDiveRad);
      flight.state.orientation.premultiply(attackRotation);
      flight.state.orientation.normalize();
      flight.state.previousOrientation.copy(flight.state.orientation);
      flight.state.velocity.applyQuaternion(attackRotation);
    }
    trail.reset();
    threats.reset();
    targetField.reset();
    targetRenderer.update(targetField.targets);
    countermeasures.reset();
    effects.reset();
    impactCam.reset();
    interceptor.kill();
    registry.clear();
    missionRunner.dispose();
    missionRunner = new MissionRunner(activeMission);
    applyMissionEffects();
  };
  spawnAircraft();

  const checksum = new Checksum();
  const currentChecksum = (): number => {
    checksum.reset();
    flight.checksum(checksum);
    controlLock.checksum(checksum);
    trail.checksum(checksum);
    threats.checksum(checksum);
    targetField.checksum(checksum);
    missionRunner.checksum(checksum);
    countermeasures.checksum(checksum);
    return checksum.value;
  };

  const loop = new Loop({
    /**
     * System execution order per tick (SPEC §4.3). Steps 5–14 arrive in later milestones and
     * insert into this sequence rather than being appended to it.
     */
    fixedUpdate: (dtS) => {
      // The shell owns the screen when it is up: the simulation must not advance behind a
      // briefing, or the player arrives already having flown thirty seconds of the mission.
      if (shell.visible || replaying) return;

      // 1. Input sampling & command filtering
      const command = input.sample(dtS);
      controlLock.tick(dtS);
      if (controlLock.active) {
        controlLock.apply(command, spawnThrottle);
        // Drain the discrete edges too, so an autopilot toggle or a countermeasure release
        // pressed during the hold does not fire the instant it lifts.
        input.clearPendingActions();
        // Keep the input's own accumulator on the trim setting: a throttle key held through the
        // hold would otherwise be integrating the whole time and jump on release.
        input.setThrottle(spawnThrottle);
      }
      if (input.consumeAction(InputAction.ToggleAutopilot)) {
        if (autopilot.engaged) autopilot.disengageAll();
        else autopilot.engageAll(flight.state);
      }
      // 2–3. Atmosphere and flight-model integration happen inside FlightModel.tick
      // 4. Autopilot, before integration
      autopilot.update(dtS, flight.state, command);
      flight.tick(dtS, command);

      // 5–6. Missile physics and interceptor AI arrive at M6/M7.
      // 7–8. Detection (10 Hz, staggered across sites) and the air defence state machines.
      const state = flight.state;
      radarTarget.positionM.copy(state.position);
      radarTarget.velocityMs.copy(state.velocity);
      radarTarget.forward.copy(state.forward());
      radarTarget.altitudeAglM = terrain.altitudeAglM(state.position);
      // 9. Countermeasure lifetimes, before the seekers read them.
      if (input.consumeAction(InputAction.Chaff)) {
        countermeasures.releaseChaff(state.position, state.velocity);
        recorder.mark(loop.time.simTimeS, 'chaff', '');
      }
      if (input.consumeAction(InputAction.Flares)) {
        countermeasures.releaseFlare(state.position, state.velocity);
        recorder.mark(loop.time.simTimeS, 'flare', '');
      }
      countermeasures.tick(dtS);
      threats.update(dtS, radarTarget, loop.time.simTimeS, countermeasures);

      // 6. Interceptor AI.
      if (interceptor.active) interceptor.tick(dtS, radarTarget, terrain);

      // 10–11. Collision against structures, and the damage it resolves to. A strike ends the
      // run, so the impact cam takes over from here.
      if (!impactCam.isPlaying) {
        impactCam.record(state);
        targetField.update(dtS, state.previousPosition, state.position, state.velocity);
      }

      // Flight recorder at 20 Hz, from the fixed tick so a recording never depends on the
      // display rate (SPEC §3.15).
      recorder.sample(dtS, loop.time.simTimeS, state, radarTarget.altitudeAglM);

      // 12. Mission logic: objectives and triggers, decimated to 10 Hz inside the runner.
      threats.stateSnapshot(threatStates);
      missionRunner.update(dtS, {
        positionM: state.position,
        altitudeAglM: radarTarget.altitudeAglM,
        integrity: state.integrity,
        fuelFraction: state.fuelKg / airframe.fuelKg,
        crashed: flight.crashed,
        threatStates,
        photographed: NO_PHOTOGRAPHS,
      });
      applyMissionEffects();

      // Debug instrumentation, last
      trail.tick(dtS, flight.state.position);
    },

    render: (alpha, time) => {
      renderer.syncToCanvasSize();
      if (hud.syncToCanvasSize() || camera.widthPx !== glCanvas.clientWidth) {
        camera.resize(glCanvas.clientWidth, glCanvas.clientHeight);
      }

      /**
       * The pause toggle is read here, not in `fixedUpdate`.
       *
       * Pausing stops the fixed tick outright, so an `Escape` sampled there would be read on the
       * way in and never on the way out — the pause would be one-way. Look-around already
       * establishes the pattern: input that belongs to the presentation runs on the render clock.
       */
      if (!shell.visible || shell.currentScreen === Screen.Paused) {
        if (input.consumeAction(InputAction.PauseMenu)) setPaused(!loop.isPaused);
      }

      // Look-around and zoom run on the render clock: they are presentation, never simulation,
      // so they keep working while the debug pause is holding the world still.
      const viewDtS = Math.min(loop.frameMs / 1000, SIM.MAX_FRAME_DT);
      camera.updateView(viewDtS, input.sampleView(), input.isActionHeld(InputAction.Zoom));

      // Interpolate the aircraft transform between the last two ticks, then hang the camera on
      // it. Interpolating here rather than in the tick is what removes 120/60 Hz judder.
      const state = flight.state;
      cameraPositionScratch.lerpVectors(state.previousPosition, state.position, alpha);
      cameraOrientationScratch.copy(state.previousOrientation).slerp(state.orientation, alpha);
      // A replay drives the view instead, and it has to do it *here*: everything below builds the
      // world around `cameraPositionScratch` — the sky dome follows it, the ocean grid and the
      // scatter recentre on it, and the terrain quadtree culls against the camera frustum. Moving
      // the camera after all that leaves it looking at a world still assembled around the wreck,
      // which renders as an empty frame the exposure meter then opens up to flat white.
      if (replaying) {
        replay.update(viewDtS);
        cameraPositionScratch.copy(replay.positionM);
        cameraOrientationScratch.copy(replay.orientation);
      }
      camera.attachTo(cameraPositionScratch, cameraOrientationScratch);

      // The camera matrices must be current before terrain selection: the quadtree culls
      // against this frame's frustum, and a stale one shows up as patches popping in at the edge.
      camera.camera.updateMatrixWorld(true);
      camera.camera.updateProjectionMatrix();

      sky.follow(cameraPositionScratch);
      terrainRenderer.update(camera.camera, sky);
      scatter.update(cameraPositionScratch);
      ocean.update(cameraPositionScratch, time.simTimeS, sky);
      // Reuses one scratch record rather than building a literal per frame (CLAUDE.md: the render
      // loop allocates nothing).
      const checkpoint = missionRunner.nextCheckpoint();
      if (checkpoint === null) {
        markers.update(null, targetField.targets, time.simTimeS);
      } else {
        checkpointScratch.positionM = checkpoint.positionM;
        checkpointScratch.radiusM = checkpoint.radiusM;
        checkpointScratch.groundM = terrain.surfaceHeightAt(
          checkpoint.positionM.x,
          checkpoint.positionM.z,
        );
        markers.update(checkpointScratch, targetField.targets, time.simTimeS);
      }
      trail.interpolate(alpha, SIM.FIXED_DT);
      effects.update(threats.missiles, countermeasures.active, viewDtS, alpha);
      effects.updateFires(targetField.targets, viewDtS);
      effects.updateDamageSmoke(state.position, state.velocity, 1 - state.integrity, viewDtS);

      // Telemetry runs before the render, not after it: the post stack and the HUD have to agree
      // on this frame's link quality, or the picture would break up a frame before the bar fell.
      threatTelemetry.jamming = threats.ew.jamming;
      threatTelemetry.linkQuality = threats.ew.linkQuality;
      threatTelemetry.gpsErrorM.copy(threats.ew.gpsErrorM);
      threatTelemetry.rwrContacts = threats.buildRwrContacts(state.headingRad, state.position);
      threatTelemetry.missileWarnings = threats.buildMissileWarnings(state.headingRad, state.position);
      threatTelemetry.chaffRemaining = countermeasures.chaffRemaining;
      threatTelemetry.flaresRemaining = countermeasures.flaresRemaining;

      const frame = telemetry.update(state, time.simTimeS, threatTelemetry);

      // Mission overlay: the radio timer runs on the render clock so a call lasts the seconds the
      // mission asked for regardless of the simulation rate.
      soundscape.update(
        viewDtS,
        {
          positionM: state.position,
          velocityMs: state.velocity,
          throttle: state.throttleActual,
          airspeedMs: state.airspeedMs,
          stallDepth: state.stallDepth,
          loadFactor: state.loadFactor,
          engineRunning: state.engineRunning,
        },
        threatTelemetry.rwrContacts,
        threatTelemetry.missileWarnings.length > 0,
      );

      missionRunner.triggers.tickRadio(viewDtS);
      frame.objectives = missionRunner.objectives.statuses;
      frame.controlLockS = controlLock.remainingS;
      const radio = missionRunner.triggers.effects.radio;
      frame.radioSpeaker = radio === null ? '' : radio.speaker;
      frame.radioText = radio === null ? '' : radio.text;
      frame.linkQuality = postPanel.linkQualityFor(frame.linkQuality);
      frame.exposureStops = post.exposureReadoutStops;

      postDrive.dtS = viewDtS;
      postDrive.simTimeS = time.simTimeS;
      postDrive.linkQuality = frame.linkQuality;
      postDrive.yawRateRadS = state.yawRateRadS;
      postDrive.horizontalFovRad = camera.horizontalFov;
      postDrive.overWater = terrain.sampleHeight(state.position.x, state.position.z) < 0;
      postDrive.altitudeM = cameraPositionScratch.y;
      post.update(postDrive);

      // The impact replay (SPEC §3.3.1): after the outcome is decided, and only then.
      // Named distinctly from the mission `replaying` flag — one is a 2.5 s cinematic, the other
      // is the telemetry viewer, and conflating them silences the HUD in the wrong places.
      const impactCamPlaying = impactCam.update(viewDtS, camera.camera);

      renderer.render(camera.camera);

      // The HUD is the drone's own display, so it goes with the drone: no overlay on either
      // replay, because the camera has stopped being the drone's camera.
      if (!impactCamPlaying && !replaying) {
        hud.render(frame, camera, camera.camera.quaternion, cameraPositionScratch);
      } else {
        hud.clear();
      }

      overlay.update();
      postPanel.update();
      threatDebug.update(state.position);
      // Nearest live target, for the debug navigation line.
      let nearest: (typeof targetField.targets)[number] | null = null;
      let nearestSq = Number.POSITIVE_INFINITY;
      for (let i = 0; i < targetField.targets.length; i += 1) {
        const candidate = targetField.targets[i];
        if (candidate.destroyed) continue;
        const d = candidate.aimPointM.distanceToSquared(state.position);
        if (d < nearestSq) {
          nearestSq = d;
          nearest = candidate;
        }
      }
      readout.setNavigation(
        state.position,
        nearest === null ? null : { designation: nearest.designation, positionM: nearest.aimPointM },
        state.headingRad,
        state.position.y,
      );
      readout.update(state, autopilot);
    },
  });

  const postPanel = new PostPanel(post);
  const overlay = new DebugOverlay({
    loop,
    registry,
    renderer,
    post,
    hud,
    terrainRenderer,
    checksum: currentChecksum,
    seed,
    reset: () => {
      spawnAircraft();
      loop.resetClock();
      bus.emit('sim:reset', { seed });
    },
  });
  const readout = new FlightReadout(airframe);
  readout.setVisible(false);

  input.attach(glCanvas);
  document.getElementById('boot')?.classList.add('hidden');

  // Accessibility: high-contrast HUD, and the temporary M1 numeric readout, are both on keys.
  window.addEventListener('keydown', (event) => {
    if (event.repeat) return;
    if (event.code === 'KeyC' && event.shiftKey) hud.toggleHighContrast();
    if (event.code === 'KeyN') readout.toggle();
  });

  if (params.get('debug') === '1') overlay.setVisible(true);

  /**
   * `?state=1` — publish the simulation on `window.vectorZero` for scripted flying.
   *
   * Everything is read through closures rather than captured references, because `missionRunner`
   * is *replaced* on every mission load: a bridge holding the object it saw at boot would report a
   * previous mission's objectives for the rest of the session.
   */
  if (params.get('state') === '1') {
    installStateBridge({
      loop,
      terrain,
      flightState: () => flight.state,
      crashed: () => flight.crashed,
      command: () => input.control,
      controlLock,
      targets: () => targetField.targets,
      runner: () => missionRunner,
      menuUp: () => shell.visible,
    });
    console.info('STATE BRIDGE: window.vectorZero.sample() — read-only, pooled, see StateBridge.ts');
  }
  if (params.get('selftest') === '1') {
    console.info(formatSelfTestResult(runDeterminismSelfTest(seed)));
  }

  console.info(
    `VECTOR ZERO M4 — ${airframe.name}, seed 0x${seed.toString(16).toUpperCase()}, sim ${(1 / SIM.FIXED_DT).toFixed(0)} Hz.\n` +
      `  A/D roll · W/S pitch · Q/E yaw · Shift/Ctrl throttle · H autopilot · Z zoom · right-drag look\n` +
      `  ~ debug overlay + downlink panel · [ ] sweep link quality · L release · Shift+C high contrast\n` +
      `  C chaff · V flares · N numeric readout · ?scene=intercept to tune an engagement\n` +
      `  ?scene=hudtest to tune the HUD · ?scene=impact to tune a strike\n` +
      `  ?attack=1200&dive=25 to spawn on a terminal attack run · ?post=0 for a clean image\n` +
      `  ?state=1 to publish the simulation on window.vectorZero for scripted flying`,
  );

  loop.start();
}

/** Show progress on the boot screen; the theatre takes a moment to arrive. */
function setBootMessage(message: string): void {
  const boot = document.getElementById('boot');
  if (boot !== null) boot.textContent = message;
}

const TERRAIN_URL = `${import.meta.env.BASE_URL}data/kerani.bin`;

/**
 * Hot module replacement does a full page reload rather than swapping modules.
 *
 * A hot swap leaves the previous game loop running against a dead WebGL context: the old post
 * stack keeps rendering into render targets whose renderer has gone, which surfaces as a stream
 * of errors from inside Three that have nothing to do with the change that was just made. Ten
 * minutes of that is worth avoiding for the price of a reload.
 */
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload();
  });
}

main().catch((error: unknown) => {
  setBootMessage('FAILED TO LOAD THEATRE');
  console.error(error);
});
